// src/pages/ClientDetailPage.tsx — Nexus-themed client dossier with editing,
// version history + revert, rotate, and (permanently-)delete actions. Every
// action is permission-gated in the UI and re-checked server-side.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/useSession';
import { GroupBadge } from '../components/group-badge';
import { GroupPicker } from '../components/group-picker';
import { GroupListCell } from '../components/group-list-cell';
import { DEFAULT_GROUP_MAP } from '@eotir/oidcbridge-shared/forum-groups';
import type {
  BrokerClientPolicySummary,
  VersionListResponse,
  VersionRecord,
} from '@eotir/oidcbridge-shared/api-contracts';

interface ClientDetailResponse { client: BrokerClientPolicySummary; }
interface RotateResponse { client: BrokerClientPolicySummary; secret: string; }

const GUESTS_GROUP_ID = '2';

export function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useSession();
  const clientId = id ?? '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['client', clientId],
    queryFn: ({ signal }) => api<ClientDetailResponse>(`/api/clients/${clientId}`, { signal }),
    enabled: clientId.length > 0,
  });

  const { data: groupData } = useQuery({
    queryKey: ['forum-groups'],
    queryFn: ({ signal }) =>
      api<{ groupMap: Record<string, string>; source: string }>('/api/forum-groups', { signal }),
    staleTime: 5 * 60_000,
  });
  const groupMap = groupData?.groupMap ?? DEFAULT_GROUP_MAP;

  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setNewSecret(null);
    setEditing(false);
  }, [clientId]);

  const rotateMut = useMutation({
    mutationFn: () =>
      api<RotateResponse>(`/api/clients/${clientId}/rotate`, { method: 'POST' }),
    onSuccess: (res) => {
      setNewSecret(res.secret);
      qc.invalidateQueries({ queryKey: ['client', clientId] });
      qc.invalidateQueries({ queryKey: ['versions', clientId] });
    },
  });

  const [permanent, setPermanent] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () =>
      api<{ ok: true }>(
        `/api/clients/${clientId}${permanent ? '?permanent=true' : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      navigate('/');
    },
  });

  if (isLoading) return <p className="text-sm text-ink-3">Loading…</p>;
  if (error) {
    const apiErr = error as ApiError;
    if (apiErr.status === 404) {
      return (
        <div>
          <Link to="/" className="font-mono text-[11px] tracking-label uppercase text-crimson-hover no-underline">← Clients</Link>
          <h1 className="eo-h1 mt-2">Client: {clientId}</h1>
          <p className="text-sm text-ink-3 mt-4">No active client by this id.</p>
        </div>
      );
    }
    return <p className="text-sm text-err">Failed to load: {apiErr.message}</p>;
  }
  if (!data) return null;
  const c = data.client;

  return (
    <div>
      <div className="eo-eyebrow">Client Dossier</div>
      <div className="mb-3">
        <Link to="/" className="font-mono text-[11px] tracking-label uppercase text-crimson-hover no-underline hover:text-white">
          ← Clients
        </Link>
      </div>
      <h1 className="font-mono text-[24px] tracking-wide text-ink mb-1">{c.clientId}</h1>
      {c.displayName && <p className="text-[14px] text-ink-2 mb-5">{c.displayName}</p>}
      {!c.displayName && <div className="mb-5" />}

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          <div className="eo-card">
            <div className="flex items-center justify-between">
              <h2 className="eo-h2">◆ Configuration</h2>
              {can('clients', 'U') && !editing && (
                <button type="button" onClick={() => setEditing(true)} className="eo-btn eo-btn--ghost">
                  Edit
                </button>
              )}
            </div>
            {editing ? (
              <EditForm
                client={c}
                groupMap={groupMap}
                onDone={() => {
                  setEditing(false);
                  qc.invalidateQueries({ queryKey: ['client', clientId] });
                  qc.invalidateQueries({ queryKey: ['clients'] });
                  qc.invalidateQueries({ queryKey: ['versions', clientId] });
                }}
                onCancel={() => setEditing(false)}
              />
            ) : (
              <dl className="grid grid-cols-[160px_1fr] gap-y-2.5 text-[13px]">
                <dt className="eo-label">Display name</dt>
                <dd>{c.displayName ?? <span className="text-ink-4">—</span>}</dd>
                <dt className="eo-label">Redirect URIs</dt>
                <dd>
                  <ul className="space-y-1">
                    {c.redirectUris.map((u) => (
                      <li key={u} className="font-mono break-all text-ink-2">{u}</li>
                    ))}
                  </ul>
                </dd>
                <dt className="eo-label">
                  {c.groupMode === 'deny' ? 'Denied Groups' : 'Allowed Groups'}
                </dt>
                <dd>
                  <GroupListCell
                    groups={c.allowedGroups}
                    groupMode={c.groupMode}
                    groupMap={groupMap}
                    collapseAfter={6}
                  />
                </dd>
                <dt className="eo-label">Alias Claims</dt>
                <dd className="font-mono">
                  {c.aliasClaims ? Object.keys(c.aliasClaims).join(', ') : <span className="text-ink-4">—</span>}
                </dd>
                {c.label && (<><dt className="eo-label">Label</dt><dd>{c.label}</dd></>)}
                {c.downstreamMode && (<><dt className="eo-label">Downstream Mode</dt><dd className="font-mono">{c.downstreamMode}</dd></>)}
                <dt className="eo-label">Secret</dt>
                <dd className="text-ink-3 italic" style={{fontFamily:'Georgia, serif'}}>Stored — visible only at create/rotate</dd>
              </dl>
            )}
          </div>

          {can('changelog', 'R') && <HistoryCard clientId={clientId} canRevert={can('clients', 'U')} />}
        </div>

        <div className="flex flex-col gap-4">
          {can('secrets', 'U') && (
            <section className="eo-card eo-card--warn">
              <h2 className="eo-h2 text-warn-soft">◆ Rotate Secret</h2>
              <p className="eo-hint mb-3.5">
                Mints a new secret, writes it to the secret store, then updates KV.
              </p>
              <button
                type="button"
                disabled={rotateMut.isPending}
                onClick={() => rotateMut.mutate()}
                className="eo-btn eo-btn--warn"
              >
                {rotateMut.isPending ? 'Rotating…' : 'Rotate'}
              </button>
              {rotateMut.error && (
                <p className="text-sm text-err mt-2">{(rotateMut.error as Error).message}</p>
              )}
              {newSecret && (
                <div className="mt-3 border border-warn bg-warn/10 p-3 rounded">
                  <p className="font-mono text-[10px] tracking-label uppercase text-warn-soft mb-1">
                    New secret — copy now, will not be displayed again
                  </p>
                  <code className="block font-mono text-sm break-all text-ink">{newSecret}</code>
                </div>
              )}
            </section>
          )}

          {can('clients', 'D') && (
            <section className="eo-card eo-card--err">
              <h2 className="eo-h2 text-err-soft">◆ Delete Client</h2>
              <p className="eo-hint mb-3.5">
                Soft delete with a 30-day restore window. Tombstoned client stops authenticating immediately.
              </p>
              {can('tombstones', 'D') && (
                <label className="flex items-center gap-2 mb-3 text-[12px] text-ink-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={permanent}
                    onChange={(e) => setPermanent(e.target.checked)}
                  />
                  Delete permanently — no tombstone, no restore window, Vault secret removed
                </label>
              )}
              <button
                type="button"
                disabled={deleteMut.isPending}
                onClick={() => {
                  if (permanent) {
                    const typed = window.prompt(
                      `PERMANENTLY delete '${c.clientId}'? No restore is possible. Type the client id to confirm:`,
                    );
                    if (typed === c.clientId) deleteMut.mutate();
                    else if (typed !== null) window.alert('Client id did not match — delete cancelled.');
                  } else if (window.confirm(`Soft-delete client '${c.clientId}'? Restorable for 30 days.`)) {
                    deleteMut.mutate();
                  }
                }}
                className="eo-btn eo-btn--err"
              >
                {deleteMut.isPending
                  ? 'Deleting…'
                  : permanent ? 'Delete permanently' : 'Delete (soft)'}
              </button>
              {deleteMut.error && (
                <p className="text-sm text-err mt-2">{(deleteMut.error as Error).message}</p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function EditForm(props: {
  client: BrokerClientPolicySummary;
  groupMap: Record<string, string>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { client: c, groupMap } = props;
  const [displayName, setDisplayName] = useState(c.displayName ?? '');
  const [redirectUrisText, setRedirectUrisText] = useState(c.redirectUris.join('\n'));
  const [groupMode, setGroupMode] = useState<'allow' | 'deny'>(c.groupMode ?? 'allow');
  const [groups, setGroups] = useState<string[]>(c.allowedGroups.map(String));
  const [aliasText, setAliasText] = useState(Object.keys(c.aliasClaims ?? {}).join(', '));
  const [pickerOpen, setPickerOpen] = useState(false);

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ client: BrokerClientPolicySummary }>(`/api/clients/${c.clientId}`, {
        method: 'PUT',
        body,
      }),
    onSuccess: props.onDone,
  });

  const apiErr = mut.error as ApiError | undefined;
  const fieldErrors = apiErr?.fieldErrors ?? {};

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const redirectUris = redirectUrisText.split('\n').map((s) => s.trim()).filter(Boolean);
    const allowedGroups = groups.map((g) => parseInt(g, 10)).filter((n) => Number.isFinite(n) && n > 0);
    const aliasKeys = aliasText.split(',').map((s) => s.trim()).filter(Boolean);
    mut.mutate({
      displayName: displayName.trim() === '' ? null : displayName.trim(),
      redirectUris,
      allowedGroups,
      groupMode,
      aliasClaims: aliasKeys.length === 0
        ? null
        : Object.fromEntries(aliasKeys.map((k) => [k, true])),
    });
  };

  const switchMode = (mode: 'allow' | 'deny') => {
    setGroupMode(mode);
    // Deny mode admits every unlisted (and future) group — always make sure
    // Guests is on the exclusion list when switching, so flipping an existing
    // allow-list to deny can't silently open the client to logged-out members.
    if (mode === 'deny') {
      setGroups((prev) => (prev.includes(GUESTS_GROUP_ID) ? prev : [GUESTS_GROUP_ID, ...prev]));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-2">
      <Field label="Display name" hint="Cosmetic label; clear to remove. The client id cannot change." error={fieldErrors.displayName}>
        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
               placeholder="e.g. Name Generator (prod)" className="eo-input" />
      </Field>
      <Field label="Redirect URIs" hint="One per line; absolute http/https URLs, no fragments" error={fieldErrors.redirectUris}>
        <textarea value={redirectUrisText} onChange={(e) => setRedirectUrisText(e.target.value)}
                  rows={3} className="eo-textarea" required />
      </Field>
      <Field label="Group policy" error={fieldErrors.groupMode ?? fieldErrors.allowedGroups}>
        <select value={groupMode}
                onChange={(e) => switchMode(e.target.value as 'allow' | 'deny')}
                className="eo-select w-72 mb-2">
          <option value="allow">Only allow these groups</option>
          <option value="deny">All groups except…</option>
        </select>
        {groupMode === 'deny' && (
          <p className="eo-hint text-warn-soft mb-2">
            Deny mode admits every group not listed — including groups created in the
            future. Keep Guests and other non-member groups in the exclusion list.
          </p>
        )}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="eo-input w-full flex items-center justify-between bg-bg-2 border border-line text-ink px-2.5 py-1.5 font-mono text-[13px] rounded cursor-pointer"
        >
          <span>
            {groups.length > 0
              ? `${groups.length} group${groups.length !== 1 ? 's' : ''} ${groupMode === 'deny' ? 'denied' : 'allowed'}`
              : 'Choose Groups…'}
          </span>
          <ChevronDown size={14} className="text-ink-3" />
        </button>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {groups.map((groupId) => (
              <GroupBadge
                key={groupId}
                groupId={groupId}
                groupName={groupMap[groupId] ?? `Group ${groupId}`}
                variant="default"
                onRemove={() => setGroups((prev) => prev.filter((g) => g !== groupId))}
              />
            ))}
          </div>
        )}
        <GroupPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          selectedGroups={groups}
          onSelectionChange={setGroups}
          groupMap={groupMap}
          isLoading={false}
        />
      </Field>
      <Field label="Alias claims" hint="Comma-separated claim keys injected as true; clear to remove" error={fieldErrors.aliasClaims}>
        <input type="text" value={aliasText} onChange={(e) => setAliasText(e.target.value)}
               placeholder="e.g. music_admin" className="eo-input font-mono" />
      </Field>

      {apiErr && !Object.keys(fieldErrors).length && (
        <p className="text-sm text-err">{apiErr.message}</p>
      )}

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={mut.isPending} className="eo-btn">
          {mut.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={props.onCancel} className="eo-btn eo-btn--ghost">Cancel</button>
      </div>
    </form>
  );
}

function HistoryCard({ clientId, canRevert }: { clientId: string; canRevert: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['versions', clientId],
    queryFn: ({ signal }) =>
      api<VersionListResponse>(`/api/clients/${clientId}/versions`, { signal }),
  });

  const revertMut = useMutation({
    mutationFn: (seq: number) =>
      api<{ client: BrokerClientPolicySummary }>(`/api/clients/${clientId}/revert`, {
        method: 'POST',
        body: { seq },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId] });
      qc.invalidateQueries({ queryKey: ['versions', clientId] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  return (
    <section className="eo-card">
      <h2 className="eo-h2">◆ History</h2>
      {isLoading && <p className="text-sm text-ink-3">Loading…</p>}
      {error && <p className="text-sm text-err">{(error as Error).message}</p>}
      {revertMut.error && <p className="text-sm text-err">{(revertMut.error as Error).message}</p>}
      {data && data.versions.length === 0 && (
        <p className="text-sm text-ink-3">
          No recorded versions yet — history starts with the first mutation after the
          versioning upgrade.
        </p>
      )}
      {data && data.versions.length > 0 && (
        <ul className="space-y-2">
          {data.versions.map((v, idx) => (
            <VersionRow
              key={v.seq}
              v={v}
              isCurrent={idx === 0}
              canRevert={canRevert}
              onRevert={() => {
                if (window.confirm(
                  `Revert '${clientId}' to v${v.seq} (${v.action} on ${new Date(v.at).toLocaleString()})? ` +
                  'The current secret is kept.',
                )) {
                  revertMut.mutate(v.seq);
                }
              }}
              reverting={revertMut.isPending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function VersionRow(props: {
  v: VersionRecord;
  isCurrent: boolean;
  canRevert: boolean;
  onRevert: () => void;
  reverting: boolean;
}) {
  const { v } = props;
  const [open, setOpen] = useState(false);
  const who = v.actorName ?? v.actorEmail ?? `member #${v.actor}`;

  return (
    <li className="border border-line rounded px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-left flex-1"
        >
          <span className="eo-chip eo-chip--mute">v{v.seq}</span>
          <span className={`eo-chip ${v.action === 'purge' || v.action === 'delete' ? 'eo-chip--err' : v.action === 'rotate' ? 'eo-chip--warn' : 'eo-chip--ops'}`}>
            {v.action}
          </span>
          <span className="text-[12px] text-ink-3">{new Date(v.at).toLocaleString()}</span>
          <span className="text-[12px] text-ink-2">{who}</span>
        </button>
        {props.canRevert && !props.isCurrent && v.snapshot && (
          <button
            type="button"
            disabled={props.reverting}
            onClick={props.onRevert}
            className="font-mono text-[10px] tracking-label uppercase text-crimson-hover hover:text-white"
          >
            Revert to this
          </button>
        )}
      </div>
      {v.note && <div className="text-[11.5px] text-ink-3 mt-1">{v.note}</div>}
      {open && (
        <div className="mt-2 space-y-1">
          {v.diff.length === 0 && <p className="text-[11.5px] text-ink-4">No field changes recorded.</p>}
          {v.diff.map((d) => (
            <div key={d.field} className="font-mono text-[11px] text-ink-2">
              <span className="text-ink">{d.field}</span>:{' '}
              <span className="text-err-soft break-all">{JSON.stringify(d.from)}</span>
              {' → '}
              <span className="text-ok-soft break-all">{JSON.stringify(d.to)}</span>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function Field(props: { label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="eo-label">{props.label}</span>
      {props.children}
      {props.hint && !props.error && <p className="eo-hint">{props.hint}</p>}
      {props.error && <p className="text-[11px] text-err mt-1.5 font-mono">{props.error}</p>}
    </label>
  );
}
